import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Tooltip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  DashboardOutlined,
  PeopleOutlined,
  EventNoteOutlined,
  AccessTimeOutlined,
  CorporateFareOutlined,
  AccountBalanceOutlined,
  AccountBalance,
  FolderOutlined,
  AssessmentOutlined,
  SettingsOutlined,
  PersonOutlined,
  AccountBalanceWalletOutlined,
  InventoryOutlined,
  SupervisedUserCircleOutlined,
  SecurityOutlined,
  MenuOutlined,
  MonitorOutlined,
  WorkOutlined,
  GroupOutlined,
  AdminPanelSettingsOutlined,
  AdminPanelSettings,
  BusinessCenterOutlined,
  CalendarTodayOutlined,
  ScheduleOutlined,
  TimelapseOutlined,
  BadgeOutlined,
  ContactsOutlined,
  PersonAddOutlined,
  ManageAccountsOutlined,
  GroupAddOutlined,
  DocumentScannerOutlined,
  DescriptionOutlined,
  FileCopyOutlined,
  CloudUploadOutlined,
  BarChartOutlined,
  InsightsOutlined,
  TuneOutlined,
  BuildOutlined,
  IntegrationInstructionsOutlined,
  VpnKeyOutlined,
  ShieldOutlined,
  LockOutlined,
  PaymentsOutlined,
  CreditCardOutlined,
  ReceiptOutlined,
  MonetizationOnOutlined,
  SavingsOutlined,
  ShoppingCartOutlined,
  LocalShippingOutlined,
  StoreOutlined,
  CategoryOutlined,
  MeetingRoomOutlined,
  EventAvailableOutlined,
  EventBusyOutlined,
  TaskOutlined,
  AssignmentTurnedInOutlined,
  FlightTakeoffOutlined,
  HotelOutlined,
  DirectionsCarOutlined,
  ReceiptLongOutlined,
  HomeWorkOutlined,
  ApartmentOutlined,
  LocationCityOutlined,
  RequestQuoteOutlined as RequestQuoteIcon,
  PieChartOutlined,
  MenuBookOutlined,
  MenuBook,
  SummarizeOutlined,
  Summarize,
  LuggageOutlined,
  Luggage,
  AddOutlined,
  Add,
  ListOutlined,
  List as ListIcon,
  ApprovalOutlined,
  Approval,
  // Additional non-outlined icons for Petty Cash
  Receipt,
  MonetizationOn,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Schedule,
  Cancel,
  Warning,
  Person,
  Business,
  Category,
  DateRange,
  AttachFile,
  PhotoCamera,
  Save,
  Send,
  Refresh,
  Assessment,
} from "@mui/icons-material";
import {
  selectUserMenus,
  selectUser,
  selectMenuLoading,
  selectIsAuthenticated,
  getUserMenus,
} from "../../../store/slices/authSlice";
import { canViewMenu } from "../../../utils/permissions";

// Comprehensive icon mapping for menu items with unique, specific icons
const iconMap = {
  // Dashboard & Main Navigation
  DashboardOutlined,
  MenuOutlined,
  HomeWorkOutlined,
  MonitorOutlined,

  // People & HR Management
  PeopleOutlined,
  PersonOutlined,
  Person,
  ContactsOutlined,
  PersonAddOutlined,
  ManageAccountsOutlined,
  GroupOutlined,
  GroupAddOutlined,
  BadgeOutlined,
  WorkOutlined,

  // Time & Attendance
  AccessTimeOutlined,
  EventNoteOutlined,
  CalendarTodayOutlined,
  ScheduleOutlined,
  TimelapseOutlined,
  EventAvailableOutlined,
  EventBusyOutlined,
  DateRange,

  // Departments & Organization
  CorporateFareOutlined,
  ApartmentOutlined,
  LocationCityOutlined,
  BusinessCenterOutlined,
  Business,

  // Finance & Accounting
  AccountBalanceOutlined,
  AccountBalance,
  AccountBalanceWalletOutlined,
  PaymentsOutlined,
  CreditCardOutlined,
  ReceiptOutlined,
  Receipt,
  MonetizationOnOutlined,
  MonetizationOn,
  SavingsOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined: RequestQuoteIcon,
  PieChartOutlined,
  MenuBookOutlined,
  MenuBook,
  SummarizeOutlined,
  Summarize,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Schedule,
  Cancel,
  Warning,

  // Documents & Files
  FolderOutlined,
  DocumentScannerOutlined,
  DescriptionOutlined,
  FileCopyOutlined,
  CloudUploadOutlined,
  AttachFile,
  PhotoCamera,
  Save,
  Send,

  // Reports & Analytics
  AssessmentOutlined,
  Assessment,
  BarChartOutlined,
  InsightsOutlined,
  Refresh,

  // Settings & Administration
  SettingsOutlined,
  TuneOutlined,
  BuildOutlined,
  IntegrationInstructionsOutlined,
  AdminPanelSettingsOutlined,
  AdminPanelSettings,

  // Security & Access
  SecurityOutlined,
  VpnKeyOutlined,
  ShieldOutlined,
  LockOutlined,
  SupervisedUserCircleOutlined,

  // Inventory & Assets
  InventoryOutlined,
  CategoryOutlined,
  Category,
  StoreOutlined,

  // Procurement & Purchasing
  ShoppingCartOutlined,
  LocalShippingOutlined,

  // Meetings & Events
  MeetingRoomOutlined,
  TaskOutlined,
  AssignmentTurnedInOutlined,

  // Travel & Expenses
  FlightTakeoffOutlined,
  HotelOutlined,
  DirectionsCarOutlined,
  LuggageOutlined,
  Luggage,

  // Budget Management Icons
  AddOutlined,
  Add,
  ListOutlined,
  List: ListIcon,
  ApprovalOutlined,
  Approval,
};

const Sidebar = ({ collapsed, onItemClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const userMenus = useSelector(selectUserMenus);
  const user = useSelector(selectUser);
  const menuLoading = useSelector(selectMenuLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Fetch menus if user is authenticated but no menus are loaded
  useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      (!userMenus || userMenus.length === 0) &&
      !menuLoading
    ) {
      dispatch(getUserMenus());
    }
  }, [dispatch, isAuthenticated, user, userMenus, menuLoading]);

  const handleMenuClick = (menu) => {
    if (menu.children && menu.children.length > 0) {
      // Toggle submenu
      setExpandedMenus((prev) => ({
        ...prev,
        [menu.id]: !prev[menu.id],
      }));
    } else if (menu.menu_url) {
      // Navigate to route
      navigate(menu.menu_url);
      if (onItemClick) {
        onItemClick();
      }
    }
  };

  const isMenuActive = (menu) => {
    if (menu.menu_url === location.pathname) {
      return true;
    }

    // Check if any child menu is active
    if (menu.children) {
      return menu.children.some((child) => isMenuActive(child));
    }

    return false;
  };

  const getMenuIcon = (iconName) => {
    // Enhanced icon mapping with fallback logic
    const IconComponent =
      iconMap[iconName] || iconMap["MenuOutlined"] || MenuOutlined;
    return <IconComponent />;
  };

  const getMenuStyling = (menu, level, isActive, hasChildren) => {
    const isParent = level === 0;
    const isChild = level > 0;

    return {
      minHeight: isParent ? 48 : 40,
      pl: isParent ? 2 : 4,
      pr: 2,
      py: 1,
      mb: isParent ? 0.5 : 0,
      mx: isParent ? 1.5 : 2,
      borderRadius: 2,
      position: "relative",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "none",

      // Background colors - Apple-like subtle
      backgroundColor: isActive
        ? isParent
          ? theme.palette.mode === 'dark' 
            ? "rgba(144, 202, 249, 0.12)"
            : "rgba(80, 118, 171, 0.12)"
          : theme.palette.mode === 'dark'
            ? "rgba(144, 202, 249, 0.08)"
            : "rgba(80, 118, 171, 0.08)"
        : "transparent",

      // Hover effects - smooth and subtle
      "&:hover": {
        backgroundColor: isActive
          ? theme.palette.mode === 'dark'
            ? "rgba(144, 202, 249, 0.15)"
            : "rgba(80, 118, 171, 0.15)"
          : theme.palette.mode === 'dark'
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.04)",
        transform: "scale(1.01)",
      },

      // Active indicator - left accent bar
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "50%",
        transform: "translateY(-50%)",
        height: isActive ? "70%" : "0%",
        width: "3px",
        bgcolor: "#5076ab",
        borderRadius: "0 2px 2px 0",
        transition: "height 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      },

      // Collapsed sidebar adjustments
      ...(collapsed &&
        level === 0 && {
          justifyContent: "center",
          px: 2,
          mx: 1,
        }),
    };
  };

  const getIconStyling = (menu, level, isActive, hasChildren) => {
    const isParent = level === 0;

    return {
      minWidth: 0,
      mr: collapsed ? 0 : 2,
      justifyContent: "center",
      color: isActive ? "#5076ab" : "#6e6e73",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",

      "& svg": {
        fontSize: isParent ? "22px" : "20px",
      },
    };
  };

  const getTextStyling = (menu, level, isActive, hasChildren) => {
    const isParent = level === 0;

    return {
      "& .MuiListItemText-primary": {
        color: isActive ? "#1d1d1f" : "#6e6e73",
        fontWeight: isActive ? 600 : 500,
        fontSize: isParent ? "15px" : "14px",
        letterSpacing: "-0.01em",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        lineHeight: 1.4,
      },
    };
  };

  const getExpandIconStyling = (isExpanded, isActive) => {
    return {
      color: isActive ? "#5076ab" : "#6e6e73",
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s",
      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
      fontSize: "20px",
    };
  };

  const renderMenuItem = (menu, level = 0) => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus[menu.id];
    const isActive = isMenuActive(menu);
    const isParent = level === 0;

    // Filter: Only show menu if user has 'view' permission
    // Backend already filters, but this is an additional safety check
    if (user && menu.menu_name && !canViewMenu(user, menu.menu_name)) {
      return null;
    }

    // Filter children that user can view
    const visibleChildren = hasChildren
      ? menu.children.filter(
          (child) => !user || !child.menu_name || canViewMenu(user, child.menu_name)
        )
      : [];

    // Don't render parent menu if it has no visible children and no direct URL
    if (hasChildren && visibleChildren.length === 0 && !menu.menu_url) {
      return null;
    }

    return (
      <React.Fragment key={menu.id}>
        <Tooltip
          title={collapsed ? menu.menu_label : ""}
          placement="right"
          disableHoverListener={!collapsed}
        >
          <ListItemButton
            onClick={() => handleMenuClick(menu)}
            selected={isActive}
            className={isParent ? "parent-menu" : "child-menu"}
            sx={getMenuStyling(menu, level, isActive, hasChildren)}
            aria-label={`${menu.menu_label} ${hasChildren ? "menu with submenu" : "menu item"}`}
            role="menuitem"
            aria-expanded={hasChildren ? isExpanded : undefined}
          >
            <ListItemIcon
              sx={getIconStyling(menu, level, isActive, hasChildren)}
            >
              {getMenuIcon(menu.menu_icon)}
            </ListItemIcon>

            {!collapsed && (
              <>
                <ListItemText
                  primary={menu.menu_label}
                  sx={getTextStyling(menu, level, isActive, hasChildren)}
                />
                {hasChildren && (
                  <ExpandMore sx={getExpandIconStyling(isExpanded, isActive)} />
                )}
              </>
            )}
          </ListItemButton>
        </Tooltip>

        {hasChildren && !collapsed && (
          <Collapse
            in={isExpanded}
            timeout={{
              enter: 400,
              exit: 250,
            }}
            unmountOnExit
            sx={{
              "& .MuiCollapse-wrapperInner": {
                mt: 0.5,
              },
            }}
          >
            <List
              component="div"
              disablePadding
              role="menu"
              aria-label={`${menu.menu_label} submenu`}
              sx={{ pt: 0.5, pb: 0.5 }}
            >
              {visibleChildren.map((child, index) => (
                <Box
                  key={child.id}
                  sx={{
                    animation: isExpanded
                      ? `fadeInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.03}s both`
                      : "none",
                    "@keyframes fadeInUp": {
                      "0%": {
                        opacity: 0,
                        transform: "translateY(-8px)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  {renderMenuItem(child, level + 1)}
                </Box>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const getUserDisplayName = () => {
    if (!user) return "User";

    const firstName = user.first_name || "";
    const surname = user.sur_name || "";

    if (firstName && surname) {
      return `${firstName} ${surname}`;
    }

    return user.email || "User";
  };

  const getUserInitials = () => {
    if (!user) return "U";

    const firstName = user.first_name || "";
    const surname = user.sur_name || "";

    if (firstName && surname) {
      return `${firstName.charAt(0)}${surname.charAt(0)}`.toUpperCase();
    }

    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return "U";
  };

  const getUserRole = () => {
    if (!user || !user.roles || user.roles.length === 0) {
      return "Employee";
    }

    // Get the first role name
    const role = user.roles[0];
    return typeof role === "string" ? role : role.role_name || "Employee";
  };

  // Default menu items if userMenus is empty (fallback)
  const defaultMenus = [
    {
      id: 1,
      menu_name: "dashboard",
      menu_label: "Dashboard",
      menu_icon: "DashboardOutlined",
      menu_url: "/dashboard",
      menu_order: 1,
      children: [],
    },
    {
      id: 2,
      menu_name: "employees",
      menu_label: "Employees",
      menu_icon: "PeopleOutlined",
      menu_url: "/employees",
      menu_order: 2,
      children: [],
    },
    {
      id: 3,
      menu_name: "leaves",
      menu_label: "Leaves",
      menu_icon: "EventNoteOutlined",
      menu_url: "/leaves",
      menu_order: 3,
      children: [],
    },
    {
      id: 4,
      menu_name: "attendance",
      menu_label: "Attendance",
      menu_icon: "AccessTimeOutlined",
      menu_url: "/attendance",
      menu_order: 4,
      children: [],
    },
    {
      id: 5,
      menu_name: "profile",
      menu_label: "Profile",
      menu_icon: "PersonOutlined",
      menu_url: "/profile",
      menu_order: 5,
      children: [],
    },
  ];

  // Show loading state while menus are being fetched
  const renderMenuSection = () => {
    if (menuLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <CircularProgress size={24} />
          {!collapsed && (
            <Typography variant="body2" sx={{ ml: 2 }} color="text.secondary">
              Loading menus...
            </Typography>
          )}
        </Box>
      );
    }

    const menusToRender =
      userMenus && userMenus.length > 0 ? userMenus : defaultMenus;

    return menusToRender.map((menu) => renderMenuItem(menu));
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "#fafafa",
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Logo and Branding */}
      <Box
        sx={{
          p: collapsed ? 1 : 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          minHeight: 72,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "white",
        }}
      >
        {!collapsed && (
          <Box sx={{ ml: 1 }}>
            <img
              src="/assets/images/logo.png"
              alt="Logo"
              style={{
                height: 48,
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          py: 2,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.2)",
            borderRadius: "3px",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        <List
          component="nav"
          sx={{
            py: 0,
          }}
        >
          {renderMenuSection()}
        </List>
      </Box>

      {/* Footer Section */}
      {!collapsed && (
        <Box
          sx={{
            p: 2.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : "white",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              textAlign: "center",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            © {new Date().getFullYear()} GH Foundation
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
